import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
  pdf,
} from "@react-pdf/renderer";
import {
  QuoteContent,
  QuoteGroup,
  calculateQuoteTotals,
} from "@/hooks/useQuotes";
import { calculateGroupTotal } from "@/hooks/useQuoteEntities";
import {
  calculateGroupDiscountTotal,
  calculateGroupGrossTotal,
} from "@/lib/finance-calculations";
import { formatShortDateRange, formatDate } from "@/lib/date-utils";
import { getQuoteLabels } from "@/lib/languages";
import logoImage from "@/assets/logo-livemotion.png";

// Register Open Sans font (using TTF format for better compatibility with react-pdf)
Font.register({
  family: "Open Sans",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/open-sans@latest/latin-300-normal.ttf",
      fontWeight: 300,
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/open-sans@latest/latin-400-normal.ttf",
      fontWeight: 400,
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/open-sans@latest/latin-400-italic.ttf",
      fontWeight: 400,
      fontStyle: "italic",
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/open-sans@latest/latin-600-normal.ttf",
      fontWeight: 600,
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/open-sans@latest/latin-700-normal.ttf",
      fontWeight: 700,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: "Open Sans",
    fontSize: 10,
    paddingBottom: 60,
    paddingHorizontal: 50,
    paddingTop: 40,
  },
  // Header section with logo and Angebot title
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  leftContainer: {},
  logo: {
    width: 155,
    height: 35,
  },
  headerRight: {
    width: "250",
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 8,
  },
  // Meta info section (company info left, quote info right)
  metaSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 25,
  },
  metaLeft: {
    fontSize: 9,
    color: "#000000",
  },
  metaRight: {
    alignItems: "flex-start",
  },
  metaRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  metaLabel: {
    fontSize: 9,
    width: 100,
  },
  metaValue: {
    fontSize: 9,
  },
  // Customer address section
  customerSection: {
    marginBottom: 25,
  },
  label: {},
  customerLabel: {
    fontSize: 9,
    marginBottom: 8,
    fontStyle: "italic",
  },
  customerName: {
    fontSize: 9,
    marginBottom: 0,
  },
  customerAddress: {
    fontSize: 9,
    marginBottom: 2,
  },
  // Title section
  titleSection: {
    marginBottom: 15,
  },
  mainTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 10,
  },
  // Project info row
  projectInfoRow: {
    flexDirection: "row",
    marginBottom: 20,
  },
  projectInfoItem: {
    flexDirection: "row",
    marginRight: 40,
  },
  projectInfoLabel: {
    fontSize: 9,
    flexBasis: "100",
    marginRight: 4,
  },
  projectInfoValue: {
    fontSize: 9,
    textAlign: "left",
    flexBasis: "100",
  },
  // Summary table styles
  summarySection: {
    marginTop: 20,
  },
  summaryTitle: {
    fontSize: 12,
    fontWeight: 700,
  },
  summaryTable: {
    width: "100%",
  },
  summaryHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    paddingBottom: 3,
    marginBottom: 4,
    alignItems: "flex-end",
  },
  summaryRow: {
    flexDirection: "row",
    paddingVertical: 1,
  },
  summaryColName: { width: "49%" },
  summaryColTotal: {
    width: "17%",
    textAlign: "right",
  },
  summaryColDiscount: {
    width: "17%",
    textAlign: "right",
  },
  summaryColNet: {
    width: "17%",
    textAlign: "right",
  },
  // Detail section heading
  detailTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 15,
  },
  // Group section
  groupSection: {
    marginBottom: 20,
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 10,
  },
  // Table styles
  table: {
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    paddingBottom: 3,
    marginBottom: 4,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 1,
  },
  subGroupHeader: {
    flexDirection: "row",
    paddingTop: 4,
    paddingBottom: 1,
  },
  colAmount: { width: "5%", textAlign: "left" },
  colFactor: { width: "5%", textAlign: "center" },
  colName: { width: "69%", paddingLeft: 10 },
  colNameNarrow: { width: "64%", paddingLeft: 10 },
  colDiscount: { width: "7%", textAlign: "right" },
  colUnitPrice: { width: "10%", textAlign: "right" },
  colTotal: { width: "10%", textAlign: "right" },
  headerText: {
    fontSize: 10,
    fontWeight: 600,
  },
  cellText: {
    fontSize: 10,
  },
  externalNoteRow: {
    paddingTop: 2,
    paddingBottom: 2,
    marginLeft: "14%", // Align with name column (6% amount + 8% factor)
  },
  externalNoteText: {
    fontSize: 10,
    fontWeight: 300,
    fontStyle: "italic",
    color: "#666666",
    paddingLeft: 10, // Match name column padding
  },
  subGroupName: {
    fontSize: 10,
    fontWeight: 700,
  },
  // Group subtotal
  groupSubtotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#000000",
    marginTop: 4,
    paddingTop: 3,
  },
  groupSubtotalLabel: {
    fontSize: 10,
    fontWeight: 700,
  },
  groupSubtotalValue: {
    fontSize: 10,
    fontWeight: 700,
  },
  // Totals section
  totalsSection: {
    marginTop: 8,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 1,
  },
  totalRowWithBorder: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 1,
    borderTopWidth: 1,
    borderTopColor: "#cccccc",
  },
  totalRowFinal: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 3,
    borderTopWidth: 2,
    borderTopColor: "#000000",
    marginTop: 3,
  },
  totalLabel: {
    fontSize: 10,
  },
  totalLabelBold: {
    fontSize: 10,
    fontWeight: 700,
  },
  totalValue: {
    fontSize: 10,
    textAlign: "right",
  },
  totalValueBold: {
    fontSize: 10,
    fontWeight: 700,
    textAlign: "right",
  },
  totalValueFinal: {
    fontSize: 11,
    fontWeight: 700,
    textAlign: "right",
  },
  // Footer
  footer: {
    position: "absolute",
    bottom: 20,
    left: 50,
    right: 50,
    borderTopWidth: 0.5,
    borderTopColor: "#000000",
    paddingTop: 6,
  },
  footerInner: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  footerLeft: {
    textAlign: "center",
    flex: 1,
  },
  footerText: {
    fontSize: 8,
    fontWeight: 300,
    color: "#000000",
    marginBottom: 2,
  },
  footerPageNumber: {
    fontSize: 8,
    fontWeight: 300,
    color: "#000000",
  },
  optionalBadge: {
    fontSize: 11,
    color: "#666666",
    fontWeight: 300,
  },
  // General Conditions section
  conditionsTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 10,
  },
  conditionsText: {
    fontSize: 9,
    fontWeight: 300,
    lineHeight: 1.6,
  },
  // Signature section
  signatureRow: {
    flexDirection: "row",
    marginTop: 40,
    gap: 30,
  },
  signatureBlock: {
    flex: 1,
  },
  signatureFieldLabel: {
    fontSize: 9,
    fontWeight: 400,
  },
  signatureUnderline: {
    borderBottomWidth: 0.5,
    borderBottomColor: "#000000",
    marginTop: 30,
    marginBottom: 4,
    width: "70%",
  },
});

export interface QuotePDFData {
  quoteName: string;
  quoteNumber: string;
  versionLabel: string;
  quoteDate: string;
  validUntil: string;
  projectDiscountPercent: number;
  vatRate: "CH_8_1" | "NONE";
  content: QuoteContent;
  deductions?: Array<{ description: string; amount: number }>;
  language?: string;
  customerName?: string;
  customerAddress?: string;
  contactName?: string;
  accountManagerName?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  planningStartDate?: string;
  planningEndDate?: string;
  customerReference?: string;
  generalConditions?: string;
}

const formatCurrency = (amount: number): string => {
  return amount.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const GroupTable = ({
  group,
  isOptional = false,
  labels,
}: {
  group: QuoteGroup;
  isOptional?: boolean;
  labels: ReturnType<typeof getQuoteLabels>;
}) => {
  const groupTotal = calculateGroupTotal(group.items);
  const regularItems = group.items.filter((item) => !item.isTitle);
  const hasDiscount = regularItems.some((item) => item.discount > 0);
  const nameCol = hasDiscount ? styles.colNameNarrow : styles.colName;

  return (
    <View style={styles.groupSection} wrap={false}>
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 8 }}>
        <Text style={styles.groupTitle}>{group.name}</Text>
        {isOptional && (
          <Text style={styles.optionalBadge}>{labels.optional}</Text>
        )}
      </View>
      <View style={styles.table}>
        {/* Table Header */}
        <View style={styles.tableHeader}>
          <Text style={[styles.headerText, styles.colAmount]}>
            {labels.amount}
          </Text>
          <Text style={[styles.headerText, styles.colFactor]}>
            {labels.factor}
          </Text>
          <Text style={[styles.headerText, nameCol]}>{labels.name}</Text>

          <Text style={[styles.headerText, styles.colUnitPrice]}>
            {labels.unitPrice}
          </Text>
          {hasDiscount && (
            <Text style={[styles.headerText, styles.colDiscount]}>
              {labels.discount}
            </Text>
          )}
          <Text style={[styles.headerText, styles.colTotal]}>
            {labels.totalCol}
          </Text>
        </View>
        {/* Table Rows */}
        {group.items.map((item) => (
          <View key={item.id}>
            {item.isTitle ? (
              <View style={styles.subGroupHeader}>
                <View style={{ width: "5%" }} />
                <View style={{ width: "5%" }} />
                <Text style={[styles.subGroupName, nameCol]}>{item.name}</Text>
              </View>
            ) : (
              <>
                <View style={styles.tableRow}>
                  <Text style={[styles.cellText, styles.colAmount]}>
                    {item.amount}
                  </Text>
                  <Text style={[styles.cellText, styles.colFactor]}>
                    {item.factor}
                  </Text>
                  <Text style={[styles.cellText, nameCol]}>{item.name}</Text>

                  <Text style={[styles.cellText, styles.colUnitPrice]}>
                    {formatCurrency(item.unitPrice)}
                  </Text>
                  {hasDiscount && (
                    <Text style={[styles.cellText, styles.colDiscount]}>
                      {item.discount > 0 ? `${item.discount}%` : ""}
                    </Text>
                  )}
                  <Text style={[styles.cellText, styles.colTotal]}>
                    {formatCurrency(item.total)}
                  </Text>
                </View>
                {/* External note - always shown if present, spanning from name column */}
                {item.externalNote && (
                  <View style={styles.externalNoteRow}>
                    <Text style={styles.externalNoteText}>
                      {item.externalNote}
                    </Text>
                  </View>
                )}
              </>
            )}
          </View>
        ))}
      </View>
      {/* Group Subtotal */}
      <View style={styles.groupSubtotal}>
        <Text style={styles.groupSubtotalLabel}>
          {labels.total} {group.name}:
        </Text>
        <Text style={styles.groupSubtotalValue}>
          {formatCurrency(groupTotal)}
        </Text>
      </View>
    </View>
  );
};

const QuotePDFDocument = ({ data }: { data: QuotePDFData }) => {
  const totals = calculateQuoteTotals(
    data.content,
    data.projectDiscountPercent,
    data.vatRate,
    data.deductions || [],
  );
  const nonOptionalGroups = data.content.groups.filter((g) => !g.optional);
  const optionalGroups = data.content.groups.filter((g) => g.optional);
  const labels = getQuoteLabels(data.language || "de");

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with Logo and Title */}
        <View style={styles.headerSection}>
          <View style={styles.leftContainer}>
            <Image style={styles.logo} src={logoImage} />

            <View>
              <Text style={styles.metaLeft}>www.livemotion.ch</Text>
              <Text style={styles.metaLeft}>info@livemotion.ch</Text>
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.headerTitle}>{labels.title}</Text>

            <View style={styles.metaSection}>
              <View style={styles.metaRight}>
                <View style={styles.metaRow}>
                  <Text style={styles.metaLabel}>{labels.quoteNumber}</Text>
                  <Text style={styles.metaValue}>{data.quoteNumber}</Text>
                </View>
                <View style={styles.metaRow}>
                  <Text style={styles.metaLabel}>{labels.quoteDate}</Text>
                  <Text style={styles.metaValue}>
                    {formatDate(data.quoteDate)}
                  </Text>
                </View>
                <View style={styles.metaRow}>
                  <Text style={styles.metaLabel}>{labels.validUntil}</Text>
                  <Text style={styles.metaValue}>
                    {formatDate(data.validUntil)}
                  </Text>
                </View>
              </View>
            </View>

            {/* Customer Address Section */}
            {data.customerName && (
              <View style={styles.customerSection}>
                <Text style={styles.customerLabel}>
                  {labels.customerAddress}
                </Text>
                <Text style={styles.customerName}>{data.customerName}</Text>
                {data.customerAddress && (
                  <Text style={styles.customerAddress}>
                    {data.customerAddress}
                  </Text>
                )}
              </View>
            )}
          </View>
        </View>

        {/* Title Section */}
        <View style={styles.titleSection}>
          <Text style={styles.mainTitle}>
            {labels.offerTitle} {data.quoteName}
          </Text>
        </View>

        {/* Project Info Row */}
        <View style={styles.projectInfoRow}>
          <View style={{ width: "50%" }}>
            {data.customerReference && (
              <View style={styles.projectInfoItem}>
                <Text style={styles.projectInfoLabel}>
                  {labels.yourReference}
                </Text>
                <Text style={styles.projectInfoValue}>
                  {data.customerReference}
                </Text>
              </View>
            )}
            {data.contactName && (
              <View style={styles.projectInfoItem}>
                <Text style={styles.projectInfoLabel}>
                  {labels.yourContact}
                </Text>
                <Text style={styles.projectInfoValue}>{data.contactName}</Text>
              </View>
            )}
            {data.accountManagerName && (
              <View style={styles.projectInfoItem}>
                <Text style={styles.projectInfoLabel}>{labels.ourContact}</Text>
                <Text style={styles.projectInfoValue}>
                  {data.accountManagerName}
                </Text>
              </View>
            )}
          </View>
          {(data.eventStartDate || data.eventEndDate) && (
            <View style={styles.projectInfoItem}>
              <Text style={styles.projectInfoLabel}>{labels.eventDate}</Text>
              <Text style={styles.projectInfoValue}>
                {formatShortDateRange(data.eventStartDate, data.eventEndDate)}
              </Text>
            </View>
          )}
        </View>

        {/* ── Summary Section ── */}
        <View style={styles.summarySection}>
          <View style={styles.summaryTable}>
            <View style={styles.summaryHeader}>
              <Text
                style={[
                  styles.headerText,
                  styles.summaryColName,
                  styles.summaryTitle,
                ]}
              >
                {labels.overview}
              </Text>
              <Text style={[styles.headerText, styles.summaryColTotal]}>
                {labels.totalCol}
              </Text>
              <Text style={[styles.headerText, styles.summaryColDiscount]}>
                {labels.discount}
              </Text>
              <Text style={[styles.headerText, styles.summaryColNet]}>
                {labels.netPrice}
              </Text>
            </View>
            {nonOptionalGroups.map((group) => {
              const groupGrossTotal = calculateGroupGrossTotal(group.items);
              const groupDiscountTotal = calculateGroupDiscountTotal(
                group.items,
              );
              const groupTotal = calculateGroupTotal(group.items);
              return (
                <View style={styles.summaryRow} key={group.id}>
                  <Text style={[styles.cellText, styles.summaryColName]}>
                    {labels.total} {group.name}
                  </Text>
                  <Text style={[styles.cellText, styles.summaryColTotal]}>
                    {formatCurrency(groupGrossTotal)}
                  </Text>
                  <Text style={[styles.cellText, styles.summaryColDiscount]}>
                    {groupDiscountTotal > 0
                      ? `- ${formatCurrency(groupDiscountTotal)}`
                      : ""}
                  </Text>
                  <Text style={[styles.cellText, styles.summaryColNet]}>
                    {formatCurrency(groupTotal)}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>

        {/* Totals Section */}
        <View style={styles.totalsSection} wrap={false}>
          <View style={styles.totalRowWithBorder}>
            <Text style={styles.totalLabelBold}>{labels.sum}</Text>
            <Text style={styles.totalValueBold}>
              {labels.currencyPrefix} {formatCurrency(totals.netTotal)}
            </Text>
          </View>

          {data.projectDiscountPercent > 0 && (
            <>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>
                  {data.projectDiscountPercent}% {labels.projectDiscount}
                </Text>
                <Text style={styles.totalValue}>
                  - {labels.currencyPrefix}{" "}
                  {formatCurrency(totals.discountAmount)}
                </Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>{labels.priceExclVat}</Text>
                <Text style={styles.totalValue}>
                  {labels.currencyPrefix}{" "}
                  {formatCurrency(totals.discountedTotal)}
                </Text>
              </View>
            </>
          )}

          {/* Deduction Items */}
          {data.deductions && data.deductions.length > 0 && (
            <>
              {data.deductions.map((deduction, index) => (
                <View key={index} style={styles.totalRow}>
                  <Text style={styles.totalLabel}>{deduction.description}</Text>
                  <Text style={styles.totalValue}>
                    - {labels.currencyPrefix} {formatCurrency(deduction.amount)}
                  </Text>
                </View>
              ))}
              {totals.deductionsTotal > 0 && (
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>{labels.priceExclVat}</Text>
                  <Text style={styles.totalValue}>
                    {labels.currencyPrefix}{" "}
                    {formatCurrency(totals.totalAfterDeductions)}
                  </Text>
                </View>
              )}
            </>
          )}

          {data.vatRate === "CH_8_1" ? (
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>
                {labels.vat} ({totals.vatRateValue}%):
              </Text>
              <Text style={styles.totalValue}>
                {labels.currencyPrefix} {formatCurrency(totals.vatAmount)}
              </Text>
            </View>
          ) : (
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>{labels.vatReverseCharge}</Text>
              <Text style={styles.totalValue}>
                {labels.currencyPrefix} 0.00
              </Text>
            </View>
          )}

          <View style={styles.totalRowFinal}>
            <Text style={styles.totalLabelBold}>{labels.priceInclVat}</Text>
            <Text style={styles.totalValueFinal}>
              {labels.currencyPrefix} {formatCurrency(totals.grossTotal)}
            </Text>
          </View>
        </View>

        {/* ── Options Section ── */}
        {optionalGroups.length > 0 && (
          <View style={styles.summarySection} wrap={false}>
            <View style={styles.summaryTable}>
              <View style={styles.summaryHeader}>
                <Text
                  style={[
                    styles.headerText,
                    styles.summaryColName,
                    styles.summaryTitle,
                  ]}
                >
                  {labels.options}
                </Text>
                <Text style={[styles.headerText, styles.summaryColTotal]}>
                  {labels.totalCol}
                </Text>
                <Text style={[styles.headerText, styles.summaryColDiscount]}>
                  {labels.discount}
                </Text>
                <Text style={[styles.headerText, styles.summaryColNet]}>
                  {labels.netPrice}
                </Text>
              </View>
              {optionalGroups.map((group) => {
                const groupGrossTotal = calculateGroupGrossTotal(group.items);
                const groupDiscountTotal = calculateGroupDiscountTotal(
                  group.items,
                );
                const groupTotal = calculateGroupTotal(group.items);
                return (
                  <View style={styles.summaryRow} key={group.id}>
                    <Text style={[styles.cellText, styles.summaryColName]}>
                      {group.name}
                    </Text>
                    <Text style={[styles.cellText, styles.summaryColTotal]}>
                      {formatCurrency(groupGrossTotal)}
                    </Text>
                    <Text style={[styles.cellText, styles.summaryColDiscount]}>
                      {groupDiscountTotal > 0
                        ? `- ${formatCurrency(groupDiscountTotal)}`
                        : ""}
                    </Text>
                    <Text style={[styles.cellText, styles.summaryColNet]}>
                      {formatCurrency(groupTotal)}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* ── Detail Section (new page) ── */}
        <View break>
          <Text style={styles.detailTitle}>{labels.detail}</Text>
        </View>

        {/* Non-optional Groups */}
        {nonOptionalGroups.map((group) => (
          <GroupTable key={group.id} group={group} labels={labels} />
        ))}

        {/* Optional Groups */}
        {optionalGroups.length > 0 && (
          <>
            {optionalGroups.map((group) => (
              <GroupTable
                key={group.id}
                group={group}
                isOptional
                labels={labels}
              />
            ))}
          </>
        )}

        {/* ── General Conditions & Signature (new page) ── */}
        {data.generalConditions && (
          <View break>
            <Text style={styles.conditionsTitle}>
              {labels.generalConditions}
            </Text>
            <Text style={styles.conditionsText}>{data.generalConditions}</Text>

            {/* Signature Area — customer only */}
            <View style={styles.signatureRow}>
              <View style={styles.signatureBlock}>
                <View style={styles.signatureUnderline} />
                <Text style={styles.signatureFieldLabel}>
                  {labels.signatureDatePlace}
                </Text>
              </View>
              <View style={styles.signatureBlock}>
                <View style={styles.signatureUnderline} />
                <Text style={styles.signatureFieldLabel}>
                  {labels.signatureField}
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <View style={styles.footerInner}>
            <View style={styles.footerLeft}>
              <Text style={styles.footerText}>
                livemotion gmbh, Bolacker 1, 4563 Gerlafingen, CHE-238.506.059
                MWST
              </Text>
              <Text style={styles.footerText}>
                IBAN CH31 8080 8008 6983 2167 4, SWIFT-BIC: RAIFCH22,
                Raiffeisenbank, 8917 Oberlunkhofen
              </Text>
            </View>
            <Text
              style={styles.footerPageNumber}
              render={({ pageNumber, totalPages }) =>
                `${pageNumber}/${totalPages}`
              }
            />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export async function generateQuotePDFBlob(data: QuotePDFData): Promise<Blob> {
  return await pdf(<QuotePDFDocument data={data} />).toBlob();
}

export async function uploadPDFToS3(
  blob: Blob,
  fileName: string,
): Promise<string> {
  // Upload via proxy to S3
  const res = await fetch("/api/storage/upload", {
    method: "POST",
    headers: {
      "Content-Type": "application/pdf",
      "X-File-Name-Encoded": encodeURIComponent(fileName),
    },
    body: blob,
  });
  if (!res.ok) throw new Error("Failed to upload PDF");
  const { publicUrl } = await res.json();

  return publicUrl;
}

export async function generateQuotePDF(data: QuotePDFData): Promise<void> {
  const blob = await generateQuotePDFBlob(data);

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${data.quoteNumber}-${data.versionLabel}_${data.quoteName.replace(/\s+/g, "_")}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export { QuotePDFDocument };
